import React, { useState } from 'react';
import {
    Box,
    Select,
    MenuItem,
    FormControl,
    Chip,
    Checkbox,
    ListItemText,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';

/**
 * A modular multi-select component that displays selected items as chips.
 * @param {object} props - The component props.
 * @param {string} props.label - The label for the input.
 * @param {Array<object>} props.options - The array of options to display. Each object should have 'label' and 'value' keys.
 * @param {Array<string>} props.selectedValues - The array of currently selected values.
 * @param {Function} props.onChange - The callback function to update the parent's state.
 * @param {boolean} [props.required=false] - Whether the field is required.
 * @param {Function} [props.onBeforeChange] - Optional callback before change (can return false to prevent change, or a Promise that resolves to boolean).
 * @param {Function} [props.isOptionDisabled] - Optional function to determine if an option should be disabled. Receives (option, selectedValues).
 */
const ChipSelect = ({ label, options, selectedValues, onChange, required = false, onBeforeChange, isOptionDisabled }) => {
    const [pendingValue, setPendingValue] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState('');

    // Handles changes to the selection
    const handleChange = async (event) => {
        const { target: { value } } = event;
        const newValue = typeof value === 'string' ? value.split(',') : value;

        // If onBeforeChange is provided, call it first
        if (onBeforeChange) {
            const result = await onBeforeChange(newValue, selectedValues);

            // If result is an object with shouldConfirm, show confirmation dialog
            if (result && typeof result === 'object' && result.shouldConfirm) {
                setPendingValue(newValue);
                setConfirmMessage(result.message || 'Are you sure you want to continue?');
                setConfirmDialogOpen(true);
                return;
            }

            // If result is false, prevent the change
            if (result === false) {
                return;
            }
        }

        onChange(newValue);
    };

    const handleConfirm = () => {
        if (pendingValue !== null) {
            onChange(pendingValue);
            setPendingValue(null);
        }
        setConfirmDialogOpen(false);
    };

    const handleCancel = () => {
        setPendingValue(null);
        setConfirmDialogOpen(false);
    };

    // Handles deleting a chip
    const handleDelete = (itemToDelete) => {
        const newSelectedValues = selectedValues.filter((item) => item !== itemToDelete);
        onChange(newSelectedValues);
    };

    // Generates a unique ID from the label to be used for accessibility
    const componentId = label.toLowerCase().replace(/\s+/g, '-');

    return (
        <>
            <FormControl fullWidth>
                <InputLabel id={`${componentId}-label`} required={required}>
                    {label}
                </InputLabel>
                <Select
                    multiple
                    labelId={`${componentId}-label`}
                    id={componentId}
                    value={selectedValues}
                    onChange={handleChange}
                    label={label}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                                // Find the option object to display its user-friendly label
                                const option = options.find(opt => opt.value === value);
                                return (
                                    <Chip
                                        key={value}
                                        label={option ? option.label : value}
                                        onDelete={() => handleDelete(value)}
                                        onMouseDown={(event) => {
                                            event.stopPropagation();
                                        }}
                                        sx={{
                                            backgroundColor: '#500000',
                                            color: 'white',
                                            '& .MuiChip-deleteIcon': {
                                                color: 'rgba(255, 255, 255)',
                                                '&:hover': {
                                                    color: 'white',
                                                },
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                >
                    {options.map((option) => {
                        const isDisabled = isOptionDisabled ? isOptionDisabled(option, selectedValues) : false;
                        return (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                disabled={isDisabled}
                                sx={{
                                    opacity: isDisabled ? 0.4 : 1,
                                    '&.Mui-disabled': {
                                        opacity: 0.4,
                                    }
                                }}
                            >
                                <Checkbox
                                    checked={selectedValues.indexOf(option.value) > -1}
                                    disabled={isDisabled}
                                />
                                <ListItemText primary={option.label} />
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCancel}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title" sx={{ color: '#500000', fontWeight: 600 }}>
                    Confirm Selection
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        {confirmMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={handleCancel} variant="outlined" sx={{ borderColor: '#500000', color: '#500000' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} variant="contained" autoFocus sx={{ backgroundColor: '#500000', '&:hover': { backgroundColor: '#3a0000' } }}>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ChipSelect;